import type { Id } from "@convex/_generated/dataModel";
import type { ProductCardDisplay } from "@/components/features/listings/product-card-display";

export type BrowserListing = {
  id: Id<"products">;
  buyerUnitPriceCents: number;
  checkoutEnabled: boolean;
  distanceMiles: number | null;
  cityName: string | null;
  stateAbbr: string;
  cityLatitude: number | null;
  cityLongitude: number | null;
  condition: { key: string; label: string; order: number } | null;
  size: { key: string; label: string } | null;
  card: ProductCardDisplay;
};

export const LOCATION_PAGE_SIZE = 12;

export type ListingSort = "default" | "price-asc" | "price-desc";

export const LISTING_SORTS: readonly ListingSort[] = [
  "default",
  "price-asc",
  "price-desc",
];

export const DISTANCE_OPTIONS_MILES: readonly number[] = [50, 100, 250, 500];

export type ListingFilters = {
  query: string;
  conditions: string[];
  sizes: string[];
  minPriceCents: number | null;
  maxPriceCents: number | null;
  maxDistanceMiles: number | null;
  sort: ListingSort;
};

export const DEFAULT_LISTING_FILTERS: ListingFilters = {
  query: "",
  conditions: [],
  sizes: [],
  minPriceCents: null,
  maxPriceCents: null,
  maxDistanceMiles: null,
  sort: "default",
};

export type FilterOption = { value: string; label: string; count: number };

export type ListingFilterOptions = {
  conditions: FilterOption[];
  sizes: FilterOption[];
  priceRangeCents: { min: number; max: number } | null;
  priceHistogram: number[];
};

const PRICE_HISTOGRAM_BUCKETS = 24;

function tally(
  facets: ReadonlyArray<{ key: string; label: string } | null>,
): Map<string, FilterOption> {
  const options = new Map<string, FilterOption>();
  for (const facet of facets) {
    if (!facet) continue;
    const existing = options.get(facet.key);
    if (existing) existing.count += 1;
    else options.set(facet.key, { value: facet.key, label: facet.label, count: 1 });
  }
  return options;
}

function priceHistogram(prices: number[], min: number, max: number): number[] {
  const buckets = new Array<number>(PRICE_HISTOGRAM_BUCKETS).fill(0);
  const span = max - min;
  for (const price of prices) {
    const index =
      span === 0
        ? 0
        : Math.min(
            Math.floor(((price - min) / span) * PRICE_HISTOGRAM_BUCKETS),
            PRICE_HISTOGRAM_BUCKETS - 1,
          );
    buckets[index] = (buckets[index] ?? 0) + 1;
  }
  return buckets;
}

function conditionOrders(cards: readonly BrowserListing[]): Map<string, number> {
  return new Map(
    cards.flatMap((card) => (card.condition ? [[card.condition.key, card.condition.order] as const] : [])),
  );
}

export function buildFilterOptions(cards: readonly BrowserListing[]): ListingFilterOptions {
  const orderOf = conditionOrders(cards);
  const conditions = [...tally(cards.map((card) => card.condition)).values()].sort(
    (left, right) =>
      (orderOf.get(left.value) ?? 0) - (orderOf.get(right.value) ?? 0) || right.count - left.count,
  );
  const sizes = [...tally(cards.map((card) => card.size)).values()].sort(
    (left, right) => right.count - left.count,
  );
  const prices = cards.map((card) => card.buyerUnitPriceCents);
  if (prices.length === 0) {
    return { conditions, sizes, priceRangeCents: null, priceHistogram: [] };
  }
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return {
    conditions,
    sizes,
    priceRangeCents: { min, max },
    priceHistogram: priceHistogram(prices, min, max),
  };
}

function matchesFacet(selected: readonly string[], facet: { key: string } | null): boolean {
  return selected.length === 0 || (facet !== null && selected.includes(facet.key));
}

function normalizeSearchText(text: string): string {
  return text.toLowerCase().replaceAll("×", "x");
}

function queryTerms(query: string): string[] {
  return normalizeSearchText(query)
    .replace(/(\d)\s*x\s*(\d)/g, "$1x$2")
    .split(/\s+/)
    .filter((term) => term.length > 0);
}

function searchableText(card: BrowserListing): string {
  return normalizeSearchText(
    [card.card.name, card.cityName, card.stateAbbr, card.condition?.label, card.size?.key, card.size?.label]
      .filter((part) => part)
      .join(" "),
  );
}

function matchesQuery(card: BrowserListing, terms: readonly string[]): boolean {
  if (terms.length === 0) return true;
  const text = searchableText(card);
  return terms.every((term) => text.includes(term));
}

function matchesFilters(card: BrowserListing, filters: ListingFilters, terms: readonly string[]): boolean {
  if (!matchesQuery(card, terms)) return false;
  if (!matchesFacet(filters.conditions, card.condition)) return false;
  if (!matchesFacet(filters.sizes, card.size)) return false;
  const price = card.buyerUnitPriceCents;
  if (filters.minPriceCents !== null && price < filters.minPriceCents) return false;
  if (filters.maxPriceCents !== null && price > filters.maxPriceCents) return false;
  return (
    filters.maxDistanceMiles === null ||
    (card.distanceMiles !== null && card.distanceMiles <= filters.maxDistanceMiles)
  );
}

function compareBySort(
  sort: ListingSort,
): ((left: BrowserListing, right: BrowserListing) => number) | null {
  switch (sort) {
    case "price-asc":
      return (left, right) => left.buyerUnitPriceCents - right.buyerUnitPriceCents;
    case "price-desc":
      return (left, right) => right.buyerUnitPriceCents - left.buyerUnitPriceCents;
    case "default":
      return null;
  }
}

export function applyListingFilters(
  cards: readonly BrowserListing[],
  filters: ListingFilters,
): BrowserListing[] {
  const terms = queryTerms(filters.query);
  const matching = cards.filter((card) => matchesFilters(card, filters, terms));
  const compare = compareBySort(filters.sort);
  return compare ? matching.sort(compare) : matching;
}

export function countActiveFilters(filters: ListingFilters): number {
  return [
    filters.conditions.length > 0,
    filters.sizes.length > 0,
    filters.minPriceCents !== null || filters.maxPriceCents !== null,
    filters.maxDistanceMiles !== null,
  ].filter(Boolean).length;
}

export function isSearching(filters: ListingFilters): boolean {
  return queryTerms(filters.query).length > 0;
}

export function clearFilters(filters: ListingFilters): ListingFilters {
  return { ...DEFAULT_LISTING_FILTERS, sort: filters.sort, query: filters.query };
}

export function clearSearchAndFilters(filters: ListingFilters): ListingFilters {
  return { ...DEFAULT_LISTING_FILTERS, sort: filters.sort };
}
